/**
 * Centralized timeout and interval configuration
 * Single source of truth for all time-based constants in milliseconds
 * 
 * @module config/timeouts
 */

/**
 * Timeout and interval constants (all values in milliseconds)
 */
export const TIMEOUTS = {
  /** Credit cache time-to-live: 30 seconds */
  CREDIT_CACHE_TTL_MS: 30_000,
  
  /** Credit polling interval: 60 seconds */
  CREDIT_POLL_INTERVAL_MS: 60_000,
  
  /** Bid refresh evaluation tick: 5 seconds (polling module) */
  BID_REFRESH_TICK_MS: 5_000,
  
  /** Legacy bid refresh tick: 60 seconds (refresh module) */
  BID_REFRESH_TICK_LEGACY_MS: 60_000,
  
  /** Logo refresh evaluation tick: 60 seconds */
  LOGO_REFRESH_TICK_MS: 60_000,
  
  /** Price check spinner/progress duration: 15 minutes */
  PRICE_CHECK_SPINNER_MS: 15 * 60 * 1000,
  
  /** OpenAI API default timeout: 9 minutes 59 seconds */
  OPENAI_DEFAULT_TIMEOUT_MS: 599_000,
  
  /** Broken image suppression duration: 30 minutes */
  BROKEN_IMAGE_SUPPRESSION_MS: 30 * 60 * 1000,
  
  /** Countdown UI update interval: 60 seconds */
  COUNTDOWN_UPDATE_MS: 60 * 1000,
  
  /** Token expiration buffer: 30 seconds before actual expiry */
  TOKEN_EXPIRATION_BUFFER_MS: 30_000,
  
  /** Lock TTL for shared estimates: 30 seconds */
  ESTIMATE_LOCK_TTL_MS: 30_000,
  
  /** Refresh token max age: 30 days */
  REFRESH_TOKEN_MAX_AGE_MS: 30 * 24 * 60 * 60 * 1000,
  
  /** Grace period for price check completion: 30 seconds */
  PRICE_CHECK_GRACE_MS: 30_000,
} as const;

/**
 * Bid refresh intervals based on lot close time proximity
 */
export const BID_REFRESH_INTERVALS = {
  /** Default interval for lots >24h until close: 6 hours */
  DEFAULT: 6 * 60 * 60 * 1000,
  
  /** Lots within 24 hours of close: 1 hour */
  WITHIN_24H: 60 * 60 * 1000,
  
  /** Lots within 30 minutes of close: 5 seconds */
  WITHIN_30MIN: 5_000,

  /** Lots within 1 hour of close: 5 minutes */
  WITHIN_1H: 5 * 60 * 1000,
  
  /** Post-close window for final refresh: 5 minutes */
  POST_CLOSE_WINDOW: 5 * 60 * 1000,
} as const;

/**
 * Lot list polling intervals based on urgency tier.
 * Uses the minimum close time across all visible lots to determine tier.
 * Reduces Cosmos DB RU consumption for stable lots.
 */
export const POLL_INTERVALS = {
  /** Lots closing within 30 minutes: 10 seconds */
  URGENT: 10_000,

  /** Lots closing within 1 hour: 15 seconds */
  ACTIVE: 15_000,

  /** Lots closing within 24 hours: 30 seconds */
  SOON: 30_000,

  /** All lots >24 hours from close: 30 seconds */
  STABLE: 30_000,

  /** No lots visible (empty state): 10 seconds */
  EMPTY: 10_000,
} as const;

/**
 * Page load and staleness thresholds
 */
export const STALENESS = {
  /** Page considered stale after: 5 minutes */
  PAGE_LOAD_THRESHOLD_MS: 5 * 60 * 1000,
  
  /** Lot considered closed and stale after: 2 minutes past close */
  LOT_POST_CLOSE_THRESHOLD_MS: 2 * 60 * 1000,
} as const;
